import { useMemo, useState } from 'react';

import {
  faHouseMedical,
  faLocationDot,
  faUser,
} from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DataTable } from '@repo/videa-components/src/index';
import { Input } from '@repo/videa-components/src/ui/input';
import type { ColumnFiltersState, SortingState } from '@tanstack/react-table';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import type { THygieneInsightsTableData } from '../../components/HygieneTableColumns';
import { useGetHygieneInsightsTableColumns } from '../../components/HygieneTableColumns';
import { TableNavItem } from '../../components/TableNavItem';
import type {
  THygienePracticeRates,
  THygieneProviderRates,
  THygieneRegionRates,
} from '../../types';

export const HygieneInsightsTable = ({
  insightsRateData,
  isLoading,
}: {
  insightsRateData: {
    provider: THygieneProviderRates[];
    practice: THygienePracticeRates[];
    region: THygieneRegionRates[];
  };
  isLoading?: boolean;
}) => {
  const [selectedClinicalDashboard, setSelectedClinicalDashboard] = useState<
    'Provider' | 'Practice' | 'Region'
  >('Provider');

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<SortingState>([]);

  const columns = useGetHygieneInsightsTableColumns({
    drilldownLevel: selectedClinicalDashboard,
  });

  const data: THygieneInsightsTableData[] = useMemo(() => {
    if (selectedClinicalDashboard === 'Provider') {
      return insightsRateData?.provider?.map((provider) => ({
        production: provider.production,
        opportunity: provider.opportunity,
        productionRatio: provider.productionRatio,
        productionRatioChange: provider.productionRatioChange,
        estimatedHygieneValues: provider.estimatedHygieneValues,
        id: provider.providerId,
        name: provider.providerName,
        practiceName: provider.practiceName,
        providerRole: provider.providerRole,
      }));
    }
    if (selectedClinicalDashboard === 'Practice') {
      return insightsRateData?.practice?.map((practice) => ({
        production: practice.production,
        opportunity: practice.opportunity,
        productionRatio: practice.productionRatio,
        productionRatioChange: practice.productionRatioChange,
        estimatedHygieneValues: practice.estimatedHygieneValues,
        id: practice.practiceId,
        name: practice.practiceName,
      }));
    }
    return insightsRateData?.region?.map((region) => ({
      production: region.production,
      opportunity: region.opportunity,
      productionRatio: region.productionRatio,
      productionRatioChange: region.productionRatioChange,
      estimatedHygieneValues: region.estimatedHygieneValues,
      id: region.regionId,
      name: region.regionName,
    }));
  }, [insightsRateData, selectedClinicalDashboard]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    state: {
      sorting,
      columnFilters,
    },
  });

  return (
    <div className="vai-flex vai-flex-col vai-gap-4">
      <div className="vai-flex vai-flex-row vai-justify-between">
        <div className="vai-flex vai-flex-row vai-gap-2">
          <TableNavItem
            dataTestId="dso-overview-nav-item"
            onClick={() => setSelectedClinicalDashboard('Region')}
            selected={selectedClinicalDashboard === 'Region'}
          >
            <FontAwesomeIcon icon={faLocationDot} />
            Region
          </TableNavItem>
          <TableNavItem
            dataTestId="practice-nav-item"
            onClick={() => setSelectedClinicalDashboard('Practice')}
            selected={selectedClinicalDashboard === 'Practice'}
          >
            <FontAwesomeIcon icon={faHouseMedical} />
            Practice
          </TableNavItem>
          <TableNavItem
            dataTestId="provider-nav-item"
            onClick={() => setSelectedClinicalDashboard('Provider')}
            selected={selectedClinicalDashboard === 'Provider'}
          >
            <span className="vai-flex vai-items-center vai-gap-2">
              <FontAwesomeIcon icon={faUser} />
              Providers
            </span>
          </TableNavItem>
        </div>
        <div className="vai-flex vai-flex-row vai-gap-4">
          <div className="vai-relative">
            <FontAwesomeIcon
              icon={faMagnifyingGlass}
              className="vai-absolute vai-left-3 vai-top-1/2 vai-transform vai--translate-y-1/2 vai-text-gray-400 vai-z-10"
            />
            <Input
              placeholder={`Search ${selectedClinicalDashboard}s`}
              value={
                (table.getColumn('name')?.getFilterValue() as string) ?? ''
              }
              onChange={(event) =>
                table.getColumn('name')?.setFilterValue(event.target.value)
              }
              className="vai-w-full vai-pl-10 vai-pr-8"
            />
          </div>
        </div>
      </div>
      <DataTable
        table={table}
        className="vai-h-[calc(100vh-400px)]"
        isLoading={isLoading}
      />
    </div>
  );
};
